/**
 * Appends the next pages of a product-list grid while the visitor scrolls.
 *
 * The section only renders its first page server-side, which keeps the initial HTML small
 * and stops below-the-fold product images from competing with the LCP image.
 * The following pages are fetched with the Section Rendering API (`?page=n&section_id=…`).
 */
class ProductListLoader extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #loading = false;

  connectedCallback() {
    this.querySelector('.product-list-loader__fallback')?.setAttribute('hidden', '');

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadNextPage();
      },
      { rootMargin: '800px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #loadNextPage() {
    const { nextUrl, sectionId } = this.dataset;
    const list = this.parentElement?.querySelector('.resource-list');
    if (this.#loading || !nextUrl || !sectionId || !list) return;

    this.#loading = true;

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to load ${url}: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = html.querySelectorAll('.resource-list > .resource-list__item');
      list.append(...items);

      const nextLoader = html.querySelector('product-list-loader');
      if (!(nextLoader instanceof HTMLElement) || !nextLoader.dataset.nextUrl || items.length === 0) {
        this.remove();
        return;
      }

      this.dataset.nextUrl = nextLoader.dataset.nextUrl;
    } catch (error) {
      console.error(error);
      // Let visitors reach the remaining products on the collection page instead.
      this.#observer?.disconnect();
      this.querySelector('.product-list-loader__fallback')?.removeAttribute('hidden');
      return;
    } finally {
      this.#loading = false;
    }

    // Re-observing fires the callback again right away, so the next page loads
    // if the loader is still within range after the new items were added.
    this.#observer?.unobserve(this);
    this.#observer?.observe(this);
  }
}

if (!customElements.get('product-list-loader')) {
  customElements.define('product-list-loader', ProductListLoader);
}
